import {
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, switchMap } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { GoodsSkuPageVO } from '@app/api/goods-service/model/goodsSkuPageVO';
import { LotBarcodeLabelControllerService } from '@app/api/stock-service/api/lotBarcodeLabelController.service';
import { LotBarcodeLabelCreateDTO } from '@app/api/stock-service/model/lotBarcodeLabelCreateDTO';

interface SkuOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-lot-barcode-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './lot-barcode-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotBarcodeCreateDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly skuApi = inject(GoodsSkuControllerService);
  private readonly lotBarcodeApi = inject(LotBarcodeLabelControllerService);

  form!: FormGroup;
  skuOptions = signal<SkuOption[]>([]);
  skuLoading = signal(false);
  saving = signal(false);
  minDate: Date;

  private skuFilterSubject$ = new Subject<string>();

  constructor() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    this.minDate = tomorrow;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      skuCode: ['', Validators.required],
      lotValue: [''],
      expiryDate: [null],
    });

    this.skuFilterSubject$
      .pipe(
        debounceTime(300),
        switchMap((keyword) => {
          this.skuLoading.set(true);
          return this.skuApi.getList3(
            keyword,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            [3],
            undefined,
            'fuzzy',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            1,
            50,
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          const list: GoodsSkuPageVO[] = res.pageContent ?? [];
          this.skuOptions.set(
            list.map((s) => ({
              label: `${s.skuCode} — ${s.skuName}`,
              value: s.skuCode!,
            })),
          );
          this.skuLoading.set(false);
        },
        error: () => {
          this.skuLoading.set(false);
        },
      });

    this.loadInitialSkus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInitialSkus(): void {
    this.skuLoading.set(true);
    this.skuApi
      .getList3(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [3],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        1,
        50,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const list: GoodsSkuPageVO[] = res.pageContent ?? [];
          this.skuOptions.set(
            list.map((s) => ({
              label: `${s.skuCode} — ${s.skuName}`,
              value: s.skuCode!,
            })),
          );
          this.skuLoading.set(false);
        },
        error: () => {
          this.skuLoading.set(false);
        },
      });
  }

  onSkuFilter(event: any): void {
    const filterValue =
      typeof event === 'string'
        ? event
        : event?.filter ?? event?.query ?? '';
    this.skuFilterSubject$.next(filterValue);
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving.set(true);
    const formValue = this.form.value;

    const body: LotBarcodeLabelCreateDTO = {
      skuCode: formValue.skuCode,
    };
    if (formValue.lotValue) {
      body.lotValue = formValue.lotValue;
    }
    if (formValue.expiryDate) {
      const d: Date = formValue.expiryDate;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      body.expiryDate = `${yyyy}-${mm}-${dd}`;
    }

    this.lotBarcodeApi
      .create(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.saving.set(false);
          this.dialogRef.close(result);
        },
        error: (err) => {
          this.saving.set(false);
          const errMsg: string =
            err?.error?.message ?? err?.message ?? '';
          if (errMsg.includes('lot_barcode.lot_expiry_conflict')) {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'lot_barcode.lot_expiry_conflict',
              ),
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'lot_barcode.messages.create_error',
              ),
            });
          }
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
